import { getBreakFromRegion } from "@/lib/regionHelpers";
import {
  assembleFlaglerReadings,
  emptyFlaglerReadings,
  flaglerConditionsPoint,
  flaglerTimeZone,
  FLAGLER_PIER_ID,
  FLAGLER_REGION_ID,
  FLAGLER_SNAPSHOT_SCRIPT_ID,
  readingsFromPublishedForecast,
  withTideExtremes,
  type FlaglerReadings,
} from "@/lib/flaglerLandings";
import { useQuery } from "@tanstack/react-query";
import { useMarineData } from "@/lib/marine";
import { type PublicForecastResponse } from "@/lib/publicForecast";
import { tideFactor } from "@/lib/nearshore";
import {
  compass8,
  factsFromReadings,
  rateAnswer,
  type AnswerFacts,
} from "@/lib/seoAnswer";
import { tideAtTime, useTideData } from "@/lib/tides";
import { useOpenMeteoWeather } from "@/lib/weather";
import fallbackRegions from "@/lib/regionsSnapshot.json";
import type { RegionConfig } from "@/lib/regions";
import { useMemo, useState } from "react";

function readEmbeddedForecast(): PublicForecastResponse | undefined {
  if (typeof document === "undefined") return undefined;
  const node = document.getElementById(FLAGLER_SNAPSHOT_SCRIPT_ID);
  if (!node?.textContent) return undefined;
  try {
    const parsed = JSON.parse(node.textContent) as PublicForecastResponse;
    if (
      !parsed ||
      !Array.isArray(parsed.spots) ||
      typeof parsed.generated_at !== "string"
    ) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

const snapshot = fallbackRegions as unknown as Record<string, RegionConfig>;

/**
 * Live Flagler Beach readings for the landing pages. Missing pieces stay
 * null so the page can omit them. Does not change forecast scoring.
 */
export function useFlaglerReadings(enabled = true): FlaglerReadings {
  const point = flaglerConditionsPoint();
  const lat = enabled ? point.lat : null;
  const lon = enabled ? point.lon : null;
  const marine = useMarineData(lat, lon, point.breakType, {
    regionId: enabled ? FLAGLER_REGION_ID : undefined,
  });
  const weather = useOpenMeteoWeather(lat, lon);
  const tides = useTideData(lat, lon, enabled ? FLAGLER_REGION_ID : null);
  const [embedded] = useState(() => (enabled ? readEmbeddedForecast() : undefined));
  const published = useQuery({
    queryKey: ["published-forecast"],
    enabled,
    staleTime: 60_000,
    retry: 1,
    placeholderData: embedded,
    queryFn: async (): Promise<PublicForecastResponse> => {
      const response = await fetch("/api/forecast.json");
      if (!response.ok) throw new Error(`forecast ${response.status}`);
      return response.json();
    },
  });
  const pier = getBreakFromRegion(snapshot[FLAGLER_REGION_ID], FLAGLER_PIER_ID);

  return useMemo(() => {
    if (!enabled) return emptyFlaglerReadings();
    const timeZone = flaglerTimeZone();
    const nowIso = new Date().toISOString();
    const publishedReadings = readingsFromPublishedForecast(published.data);
    if (
      publishedReadings &&
      (publishedReadings.surf || publishedReadings.tide || publishedReadings.water)
    ) {
      return withTideExtremes(publishedReadings, tides.data, timeZone, nowIso);
    }
    const reading = marine.data;
    const windNow = weather.data;
    const observedAt =
      reading && marine.dataUpdatedAt
        ? new Date(marine.dataUpdatedAt).toISOString()
        : null;
    let surf: AnswerFacts | null = null;
    if (reading && windNow && observedAt) {
      const swellDirectionDeg = reading.swellDirection ?? reading.waveDirection;
      const currentTide = tideAtTime(tides.data, nowIso);
      const tideQualityFactor =
        pier?.calibration && currentTide
          ? tideFactor(currentTide.height, currentTide.trend, pier.calibration)
          : null;
      surf = factsFromReadings({
        rating: rateAnswer({
          faceHeightM: reading.waveHeightM,
          periodS: reading.swellPeriod ?? reading.wavePeriod,
          windSpeedKmh: windNow.currentWindSpeed,
          windGustKmh: windNow.currentWindGusts,
          windDirectionDeg: windNow.currentWindDirection,
          swellDirectionDeg,
          shoreNormal: pier?.shoreNormal ?? point.shoreNormal,
          tideQualityFactor,
        }),
        faceHeightM: reading.waveHeightM,
        swellHeightM: reading.swellHeightM ?? reading.offshoreHeightM,
        swellPeriodS: reading.swellPeriod ?? reading.wavePeriod,
        swellDirectionDeg,
        windSpeedKmh: windNow.currentWindSpeed,
        windDirectionDeg: windNow.currentWindDirection,
        updatedAtIso: observedAt,
        timeZone,
      });
    }
    const windDirection = windNow ? compass8(windNow.currentWindDirection) : null;
    const windObservedAt = weather.dataUpdatedAt
      ? new Date(weather.dataUpdatedAt).toISOString()
      : null;
    return assembleFlaglerReadings({
      surf,
      surfObservedAt: surf ? observedAt : null,
      waterTempC: reading?.waterTempC ?? null,
      waterObservedAt: observedAt,
      tide: tides.data,
      wind:
        windNow && windDirection && windObservedAt && finite(windNow.currentWindSpeed)
          ? {
              mph: windNow.currentWindSpeed * 0.621371,
              direction: windDirection,
              observedAt: windObservedAt,
            }
          : null,
      nowIso,
      timeZone,
    });
  }, [
    enabled,
    marine.data,
    marine.dataUpdatedAt,
    pier,
    point.shoreNormal,
    tides.data,
    weather.data,
    weather.dataUpdatedAt,
    published.data,
  ]);
}

function finite(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}
